// ErrorContext.js - Performance optimized error handling context
import React, { createContext, useReducer, useCallback, useMemo } from 'react';

// Define standard error types
export const ERROR_TYPES = {
  RATE_LIMIT: 'RATE_LIMIT_EXCEEDED',
  VALIDATION: 'VALIDATION_ERROR',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  NETWORK: 'NETWORK_ERROR',
  SERVER: 'SERVER_ERROR',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  EMAIL_EXISTS: 'EMAIL_ALREADY_REGISTERED',
  USER_NOT_FOUND: 'EMAIL_NOT_FOUND',
  PASSWORD_REQUIREMENTS: 'PASSWORD_REQUIREMENTS_NOT_MET',
  INVALID_OTP: 'INVALID_OTP',
  OAUTH_FAILED: 'OAUTH_INITIALIZATION_ERROR',
  GOOGLE_SIGNIN_FAILED: 'GOOGLE_SIGNIN_FAILED',
  GOOGLE_INFO_FAILED: 'GOOGLE_INFO_RETRIEVAL_FAILED',
  ACCOUNT_CREATION_FAILED: 'ACCOUNT_CREATION_FAILED',
  ACCOUNT_SETUP_FAILED: 'ACCOUNT_SETUP_FAILED',
  AUTO_LOGIN_FAILED: 'AUTO_LOGIN_FAILED',
  ACCOUNT_UPDATE_FAILED: 'ACCOUNT_UPDATE_FAILED',
  WRONG_PROVIDER: 'WRONG_PROVIDER',
  EMAIL_SEND_FAILED: 'EMAIL_SEND_FAILED',
  
  // Firewall specific error types
  FIREWALL_CREATE_FAILED: 'FIREWALL_CREATE_FAILED',
  FIREWALL_DELETE_FAILED: 'FIREWALL_DELETE_FAILED',
  FIREWALL_RULE_ADD_FAILED: 'FIREWALL_RULE_ADD_FAILED',
  FIREWALL_RULE_DELETE_FAILED: 'FIREWALL_RULE_DELETE_FAILED',
  FIREWALL_ATTACH_FAILED: 'FIREWALL_ATTACH_FAILED',
  FIREWALL_DETACH_FAILED: 'FIREWALL_DETACH_FAILED',
  FIREWALL_LIST_FAILED: 'FIREWALL_LIST_FAILED',
  FIREWALL_DETAILS_FAILED: 'FIREWALL_DETAILS_FAILED',
  INVALID_RULE_PARAMETERS: 'INVALID_RULE_PARAMETERS',
  REGION_NOT_AVAILABLE: 'REGION_NOT_AVAILABLE',
  
  // Volume specific error types
  VOLUME_CREATE_FAILED: 'VOLUME_CREATE_FAILED',
  VOLUME_DELETE_FAILED: 'VOLUME_DELETE_FAILED',
  VOLUME_ATTACH_FAILED: 'VOLUME_ATTACH_FAILED',
  VOLUME_DETACH_FAILED: 'VOLUME_DETACH_FAILED',
  VOLUME_LIST_FAILED: 'VOLUME_LIST_FAILED',
  VOLUME_DETAILS_FAILED: 'VOLUME_DETAILS_FAILED',
  INVALID_VOLUME_PARAMETERS: 'INVALID_VOLUME_PARAMETERS',
  VOLUME_ALREADY_EXISTS: 'VOLUME_ALREADY_EXISTS',
  VOLUME_NOT_FOUND: 'VOLUME_NOT_FOUND',
  VOLUME_IN_USE: 'VOLUME_IN_USE',
  VOLUME_SIZE_INVALID: 'VOLUME_SIZE_INVALID',
  VM_NOT_ACTIVE: 'VM_NOT_ACTIVE',
  
  // Image specific error types
  IMAGE_CREATE_FAILED: 'IMAGE_CREATE_FAILED',
  IMAGE_DELETE_FAILED: 'IMAGE_DELETE_FAILED',
  IMAGE_LIST_FAILED: 'IMAGE_LIST_FAILED',
  IMAGE_DETAILS_FAILED: 'IMAGE_DETAILS_FAILED',
  INVALID_IMAGE_PARAMETERS: 'INVALID_IMAGE_PARAMETERS',
  IMAGE_ALREADY_EXISTS: 'IMAGE_ALREADY_EXISTS',
  IMAGE_NOT_FOUND: 'IMAGE_NOT_FOUND',
  NO_SNAPSHOTS_AVAILABLE: 'NO_SNAPSHOTS_AVAILABLE',
  SNAPSHOT_NOT_FOUND: 'SNAPSHOT_NOT_FOUND',
  
  // Snapshot specific error types
  SNAPSHOT_CREATE_FAILED: 'SNAPSHOT_CREATE_FAILED',
  SNAPSHOT_DELETE_FAILED: 'SNAPSHOT_DELETE_FAILED',
  SNAPSHOT_RESTORE_FAILED: 'SNAPSHOT_RESTORE_FAILED',
  SNAPSHOT_LIST_FAILED: 'SNAPSHOT_LIST_FAILED',
  SNAPSHOT_DETAILS_FAILED: 'SNAPSHOT_DETAILS_FAILED',
  INVALID_SNAPSHOT_PARAMETERS: 'INVALID_SNAPSHOT_PARAMETERS',
  SNAPSHOT_ALREADY_EXISTS: 'SNAPSHOT_ALREADY_EXISTS',
  SNAPSHOT_IN_USE: 'SNAPSHOT_IN_USE',
  VM_UNAVAILABLE: 'VM_UNAVAILABLE',
  VM_NOT_COMPATIBLE: 'VM_NOT_COMPATIBLE',
  NO_ACTIVE_VMS: 'NO_ACTIVE_VMS',

  // VM Action error types
  VM_START_FAILED: 'VM_START_FAILED',
  VM_STOP_FAILED: 'VM_STOP_FAILED',
  VM_REBOOT_FAILED: 'VM_REBOOT_FAILED',
  VM_HIBERNATE_FAILED: 'VM_HIBERNATE_FAILED',
  VM_RESTORE_FAILED: 'VM_RESTORE_FAILED',
  VM_DELETE_FAILED: 'VM_DELETE_FAILED',
  VM_FETCH_FAILED: 'VM_FETCH_FAILED',
  VM_DETAILS_FETCH_FAILED: 'VM_DETAILS_FETCH_FAILED',
  VM_IP_TOGGLE_FAILED: 'VM_IP_TOGGLE_FAILED',

  // Credit balance error types
  CREDIT_BALANCE_FETCH_FAILED: 'CREDIT_BALANCE_FETCH_FAILED',
  CREDIT_STREAM_FAILED: 'CREDIT_STREAM_FAILED',

  // Profile and GSTIN/Tax ID error types
  TAX_ID_SAVE_FAILED: 'TAX_ID_SAVE_FAILED',
  GSTIN_SAVE_FAILED: 'GSTIN_SAVE_FAILED',
  TAX_INFO_FETCH_FAILED: 'TAX_INFO_FETCH_FAILED',
  USER_PROFILE_FETCH_FAILED: 'USER_PROFILE_FETCH_FAILED',
  
  // Profile/Account specific error types
  PROFILE_UPDATE_FAILED: 'PROFILE_UPDATE_FAILED',
  PROFILE_FETCH_FAILED: 'PROFILE_FETCH_FAILED',
  PASSWORD_CHANGE_FAILED: 'PASSWORD_CHANGE_FAILED',
  PASSWORD_MISMATCH: 'PASSWORD_MISMATCH',
  OLD_PASSWORD_INCORRECT: 'OLD_PASSWORD_INCORRECT',
  PROFILE_PICTURE_UPLOAD_FAILED: 'PROFILE_PICTURE_UPLOAD_FAILED',
  EMAIL_VERIFICATION_FAILED: 'EMAIL_VERIFICATION_FAILED',
  ACCOUNT_SETTINGS_SAVE_FAILED: 'ACCOUNT_SETTINGS_SAVE_FAILED',
  PAYMENT_METHOD_ADD_FAILED: 'PAYMENT_METHOD_ADD_FAILED',
  PAYMENT_METHOD_REMOVE_FAILED: 'PAYMENT_METHOD_REMOVE_FAILED',
  PAYMENT_METHOD_UPDATE_FAILED: 'PAYMENT_METHOD_UPDATE_FAILED',
  BILLING_HISTORY_FETCH_FAILED: 'BILLING_HISTORY_FETCH_FAILED',
  API_KEY_GENERATION_FAILED: 'API_KEY_GENERATION_FAILED',
  API_KEY_DELETION_FAILED: 'API_KEY_DELETION_FAILED',
  TWO_FACTOR_SETUP_FAILED: 'TWO_FACTOR_SETUP_FAILED',
  TWO_FACTOR_DISABLE_FAILED: 'TWO_FACTOR_DISABLE_FAILED',
  NOTIFICATION_SETTINGS_UPDATE_FAILED: 'NOTIFICATION_SETTINGS_UPDATE_FAILED',

  // Payment specific error types
  PAYMENT_INITIALIZATION_FAILED: 'PAYMENT_INITIALIZATION_FAILED',
  PAYMENT_PROCESSING_FAILED: 'PAYMENT_PROCESSING_FAILED',
  PAYMENT_VERIFICATION_FAILED: 'PAYMENT_VERIFICATION_FAILED',
  PAYMENT_CAPTURE_FAILED: 'PAYMENT_CAPTURE_FAILED',
  RAZORPAY_SDK_LOAD_FAILED: 'RAZORPAY_SDK_LOAD_FAILED',
  CREDIT_PURCHASE_FAILED: 'CREDIT_PURCHASE_FAILED',
  TRANSACTION_RETRIEVAL_FAILED: 'TRANSACTION_RETRIEVAL_FAILED',
  PAYMENT_ORDER_CREATION_FAILED: 'PAYMENT_ORDER_CREATION_FAILED',
  PAYMENT_CURRENCY_NOT_SUPPORTED: 'PAYMENT_CURRENCY_NOT_SUPPORTED',
  PAYMENT_AMOUNT_TOO_LOW: 'PAYMENT_AMOUNT_TOO_LOW',
  PAYMENT_GATEWAY_ERROR: 'PAYMENT_GATEWAY_ERROR',
  PAYMENT_SIGNATURE_VERIFICATION_FAILED: 'PAYMENT_SIGNATURE_VERIFICATION_FAILED',
  PAYMENT_WEBHOOK_PROCESSING_FAILED: 'PAYMENT_WEBHOOK_PROCESSING_FAILED',
  RAZORPAY_API_ERROR: 'RAZORPAY_API_ERROR',
  STRIPE_API_ERROR: 'STRIPE_API_ERROR',
  
  // VM deployment specific error types
  VM_DEPLOY_FAILED: 'VM_DEPLOY_FAILED',
  VM_RESIZE_FAILED: 'VM_RESIZE_FAILED',
  VM_STATUS_CHECK_FAILED: 'VM_STATUS_CHECK_FAILED',
  VM_LIST_FAILED: 'VM_LIST_FAILED',
  VM_DETAILS_FAILED: 'VM_DETAILS_FAILED',
  INVALID_VM_PARAMETERS: 'INVALID_VM_PARAMETERS',
  GPU_NOT_AVAILABLE: 'GPU_NOT_AVAILABLE',
  INSUFFICIENT_CREDITS: 'INSUFFICIENT_CREDITS',
  FLAVOR_NOT_FOUND: 'FLAVOR_NOT_FOUND',
  REGION_CAPACITY_EXCEEDED: 'REGION_CAPACITY_EXCEEDED',
  SSH_KEY_REQUIRED: 'SSH_KEY_REQUIRED',
  ADMIN_PASSWORD_REQUIRED: 'ADMIN_PASSWORD_REQUIRED',
  OS_IMAGE_REQUIRED: 'OS_IMAGE_REQUIRED',
  DEPLOYMENT_VALIDATION_FAILED: 'DEPLOYMENT_VALIDATION_FAILED',
  SECURITY_RULE_APPLICATION_FAILED: 'SECURITY_RULE_APPLICATION_FAILED',
  CLOUD_INIT_SCRIPT_INVALID: 'CLOUD_INIT_SCRIPT_INVALID',
  JUPYTER_PASSWORD_WEAK: 'JUPYTER_PASSWORD_WEAK',
  CALLBACK_REGISTRATION_FAILED: 'CALLBACK_REGISTRATION_FAILED',
  DEPLOYMENT_TIMEOUT: 'DEPLOYMENT_TIMEOUT',
  FLAVOR_INCOMPATIBLE: 'FLAVOR_INCOMPATIBLE'
};

const errorReducer = (state, action) => {
  switch (action.type) {
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        hasError: true
      };
    case 'SET_RATE_LIMIT_ERROR':
      return {
        ...state,
        error: {
          code: ERROR_TYPES.RATE_LIMIT,
          message: action.payload.message,
          retryTime: action.payload.retryTime,
          priority: 100
        },
        hasError: true
      };
    case 'SET_PROVIDER_ERROR':
      return {
        ...state,
        error: {
          code: ERROR_TYPES.WRONG_PROVIDER,
          message: action.payload.message,
          provider: action.payload.provider,
          action: action.payload.action,
          priority: 90
        },
        hasError: true
      };
    case 'SET_AUTH_ERROR':
      return {
        ...state,
        error: {
          code: action.payload.code,
          message: action.payload.message,
          details: action.payload.details,
          priority: 80
        },
        hasError: true
      };
    case 'SET_FIREWALL_ERROR':
      return {
        ...state,
        error: {
          code: action.payload.code,
          message: action.payload.message,
          details: action.payload.details,
          priority: 70
        },
        hasError: true
      };
    case 'SET_VOLUME_ERROR':
      return {
        ...state,
        error: {
          code: action.payload.code,
          message: action.payload.message,
          details: action.payload.details,
          priority: 70
        },
        hasError: true
      };
    case 'SET_IMAGE_ERROR':
      return {
        ...state,
        error: {
          code: action.payload.code,
          message: action.payload.message,
          details: action.payload.details,
          priority: 70
        },
        hasError: true
      };
    case 'SET_SNAPSHOT_ERROR':
      return {
        ...state,
        error: {
          code: action.payload.code,
          message: action.payload.message,
          details: action.payload.details,
          priority: 70
        },
        hasError: true
      };
    case 'SET_PROFILE_ERROR':
      return {
        ...state,
        error: {
          code: action.payload.code,
          message: action.payload.message,
          details: action.payload.details,
          priority: 75
        },
        hasError: true
      };
    case 'SET_VM_DEPLOYMENT_ERROR':
      return {
        ...state,
        error: {
          code: action.payload.code,
          message: action.payload.message,
          details: action.payload.details,
          priority: 85
        },
        hasError: true
      };
    case 'SET_VALIDATION_ERROR':
      return {
        ...state,
        formErrors: action.payload,
        hasError: Object.keys(action.payload).length > 0
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
        hasError: false
      };
    case 'CLEAR_VALIDATION_ERRORS':
      return {
        ...state,
        formErrors: {},
        hasError: state.error !== null
      };
    case 'CLEAR_ALL_ERRORS':
      return {
        error: null,
        formErrors: {},
        hasError: false
      };
    default:
      return state;
  }
};

const initialErrorState = {
  error: null,
  formErrors: {},
  hasError: false
};

export const ErrorContext = createContext();

export const ErrorProvider = ({ children }) => {
  const [errorState, dispatch] = useReducer(errorReducer, initialErrorState);

  const handleApiError = useCallback((error) => {
    if (!error) return;
    
    
    // Handle rate limit errors
    if (error.code === ERROR_TYPES.RATE_LIMIT) {
      dispatch({
        type: 'SET_RATE_LIMIT_ERROR',
        payload: {
          message: error.error || error.message,
          retryTime: error.retry_time || error.retryTime
        }
      });
      return;
    }
    
    // Handle wrong provider errors (special case for social auth)
    if (error.provider) {
      dispatch({
        type: 'SET_PROVIDER_ERROR',
        payload: {
          message: error.message,
          provider: error.provider,
          action: error.action
        }
      });
      return;
    }
    
    // Handle network errors
    if (error.message && (
      error.message.includes('Failed to fetch') || 
      error.code === ERROR_TYPES.NETWORK)) {
      dispatch({
        type: 'SET_AUTH_ERROR',
        payload: {
          code: ERROR_TYPES.NETWORK,
          message: 'Network connection issue. Please check your internet connection and try again.'
        }
      });
      return;
    }
    
    // Handle validation errors
    if (error.fieldErrors) {
      dispatch({
        type: 'SET_VALIDATION_ERROR',
        payload: error.fieldErrors
      });
      return;
    }
    
    // Handle VM deployment-specific errors
    if (error.code && Object.values(ERROR_TYPES).includes(error.code) && 
        (error.code.includes('VM_') || 
         error.code === ERROR_TYPES.INSUFFICIENT_CREDITS ||
         error.code === ERROR_TYPES.GPU_NOT_AVAILABLE ||
         error.code === ERROR_TYPES.FLAVOR_NOT_FOUND ||
         error.code === ERROR_TYPES.REGION_CAPACITY_EXCEEDED ||
         error.code === ERROR_TYPES.SSH_KEY_REQUIRED ||
         error.code === ERROR_TYPES.ADMIN_PASSWORD_REQUIRED ||
         error.code === ERROR_TYPES.OS_IMAGE_REQUIRED ||
         error.code === ERROR_TYPES.DEPLOYMENT_VALIDATION_FAILED ||
         error.code === ERROR_TYPES.SECURITY_RULE_APPLICATION_FAILED ||
         error.code === ERROR_TYPES.CLOUD_INIT_SCRIPT_INVALID ||
         error.code === ERROR_TYPES.JUPYTER_PASSWORD_WEAK ||
         error.code === ERROR_TYPES.CALLBACK_REGISTRATION_FAILED ||
         error.code === ERROR_TYPES.DEPLOYMENT_TIMEOUT ||
         error.code === ERROR_TYPES.FLAVOR_INCOMPATIBLE))
    {
      dispatch({
        type: 'SET_VM_DEPLOYMENT_ERROR',
        payload: {
          code: error.code,
          message: error.error || error.message,
          details: error.details
        }
      });
      return;
    }
    
    // Handle firewall-specific errors
    if (error.code && Object.values(ERROR_TYPES).includes(error.code) && 
        error.code.includes('FIREWALL')) {
      dispatch({
        type: 'SET_FIREWALL_ERROR',
        payload: {
          code: error.code,
          message: error.error || error.message,
          details: error.details
        }
      });
      return;
    }
    
    // Handle volume-specific errors
    if (error.code && Object.values(ERROR_TYPES).includes(error.code) && 
        error.code.includes('VOLUME')) {
      dispatch({
        type: 'SET_VOLUME_ERROR',
        payload: {
          code: error.code,
          message: error.error || error.message,
          details: error.details
        }
      });
      return;
    }
    
    // Handle image-specific errors
    if (error.code && Object.values(ERROR_TYPES).includes(error.code) && 
        (error.code.includes('IMAGE'))) {
      dispatch({
        type: 'SET_IMAGE_ERROR',
        payload: {
          code: error.code,
          message: error.error || error.message,
          details: error.details
        }
      });
      return;
    }
    
    // Handle snapshot-specific errors
    if (error.code && Object.values(ERROR_TYPES).includes(error.code) && 
        (error.code.includes('SNAPSHOT') || 
         error.code === ERROR_TYPES.VM_UNAVAILABLE || 
         error.code === ERROR_TYPES.VM_NOT_COMPATIBLE ||
         error.code === ERROR_TYPES.NO_ACTIVE_VMS)) {
      dispatch({
        type: 'SET_SNAPSHOT_ERROR',
        payload: {
          code: error.code,
          message: error.error || error.message,
          details: error.details
        }
      });
      return;
    }
    
    // Handle VM state errors for volume operations
    if (error.code === ERROR_TYPES.VM_NOT_ACTIVE) {
      dispatch({
        type: 'SET_VOLUME_ERROR',
        payload: {
          code: error.code,
          message: error.error || error.message,
          details: error.details
        }
      });
      return;
    }
    
    // Handle profile/account-specific errors
    if (error.code && Object.values(ERROR_TYPES).includes(error.code) && 
        (error.code.includes('PROFILE') || 
         error.code.includes('PASSWORD') || 
         error.code.includes('ACCOUNT') || 
         error.code.includes('PAYMENT') || 
         error.code.includes('BILLING') || 
         error.code.includes('API_KEY') || 
         error.code.includes('TWO_FACTOR') || 
         error.code.includes('NOTIFICATION'))) {
      dispatch({
        type: 'SET_PROFILE_ERROR',
        payload: {
          code: error.code,
          message: error.error || error.message,
          details: error.details
        }
      });
      return;
    }
    
    // Handle all other error types
    dispatch({
      type: 'SET_AUTH_ERROR',
      payload: {
        code: error.code || ERROR_TYPES.SERVER,
        message: error.error || error.message || 'An unexpected error occurred',
        details: error.details
      }
    });
  }, []);

  const getErrorMessage = useCallback((code, defaultMessage) => {
    const errorMessages = {
      // Auth error messages
      [ERROR_TYPES.RATE_LIMIT]: 'For security reasons, access has been temporarily limited. Please try again later.',
      [ERROR_TYPES.VALIDATION]: 'Please check the form for errors and try again.',
      [ERROR_TYPES.INVALID_CREDENTIALS]: 'The email or password you entered doesn\'t match our records. Please check and try again.',
      [ERROR_TYPES.NETWORK]: 'Network connection issue. Please check your internet connection and try again.',
      [ERROR_TYPES.SERVER]: 'We\'re experiencing technical difficulties. Please try again later.',
      [ERROR_TYPES.SESSION_EXPIRED]: 'Your authentication session has expired. Please try signing in again.',
      [ERROR_TYPES.EMAIL_EXISTS]: 'This email is already registered. Please sign in or use a different email address.',
      [ERROR_TYPES.USER_NOT_FOUND]: 'Email address not found. Please check and try again.',
      [ERROR_TYPES.PASSWORD_REQUIREMENTS]: 'Password does not meet the security requirements. Please use a stronger password.',
      [ERROR_TYPES.INVALID_OTP]: 'Invalid or expired verification code. Please try again.',
      [ERROR_TYPES.WRONG_PROVIDER]: 'This account uses a different sign-in method.',
      [ERROR_TYPES.EMAIL_SEND_FAILED]: 'We couldn\'t send the verification email. Please try again later.',
      
      // All other error messages...
      [ERROR_TYPES.VM_DEPLOY_FAILED]: 'Failed to deploy VM. Please check your configuration and try again.',
      [ERROR_TYPES.INSUFFICIENT_CREDITS]: 'You do not have enough credits to deploy this VM. Please add more credits to your account.',
      [ERROR_TYPES.GPU_NOT_AVAILABLE]: 'The selected GPU is not available in the chosen region. Please select a different GPU or region.'
    };
    
    return errorMessages[code] || defaultMessage || 'An error occurred';
  }, []);

  const clearErrors = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL_ERRORS' });
  }, []);

  const clearValidationErrors = useCallback(() => {
    dispatch({ type: 'CLEAR_VALIDATION_ERRORS' });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const setValidationErrors = useCallback((errors) => {
    dispatch({
      type: 'SET_VALIDATION_ERROR',
      payload: errors
    });
  }, []);

  const contextValue = useMemo(() => ({
    errorState,
    handleApiError,
    getErrorMessage,
    clearErrors,
    clearError,
    clearValidationErrors,
    setValidationErrors
  }), [
    errorState,
    handleApiError,
    getErrorMessage,
    clearErrors,
    clearError,
    clearValidationErrors,
    setValidationErrors
  ]);

  return (
    <ErrorContext.Provider value={contextValue}>
      {children}
    </ErrorContext.Provider>
  );
};

export default ErrorProvider;